/**
 * Quote Analytics — compact section on the Admin Quotes page.
 *
 * Reads from GET /api/admin/quotes/analytics/daily which buckets
 * quote_activities.created_at by America/New_York calendar day and
 * deduplicates by public_id. One quote = one count.
 *
 * Owner directive 2026-02-21: tightly scoped. Do NOT extend to
 * conversion / revenue / customer analytics without a separate task.
 */
import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";


function KpiCard({ label, value, testid }) {
  return (
    <div
      className="rounded-lg border-2 border-[var(--ink)] bg-white px-3 py-2 min-w-[110px]"
      data-testid={testid}
    >
      <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]">
        {label}
      </div>
      <div className="text-2xl font-black font-mono mt-0.5">{value}</div>
    </div>
  );
}


function DailyBarChart({ series }) {
  if (!series || series.length === 0) return null;
  const max = Math.max(1, ...series.map((d) => d.count));
  return (
    <div
      className="mt-3 border-2 border-[var(--ink)] rounded-lg p-3 bg-white"
      data-testid="quote-analytics-chart"
    >
      <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)] mb-2">
        Quotes Generated — Last {series.length} Days
      </div>
      <div className="flex items-end gap-[3px] h-32">
        {series.map((d) => {
          const pct = Math.max(2, Math.round((d.count / max) * 100));
          return (
            <div
              key={d.date}
              className="flex-1 relative group"
              title={`${d.date}: ${d.count} quote${d.count === 1 ? "" : "s"}`}
              data-testid={`quote-analytics-bar-${d.date}`}
            >
              <div
                className="w-full bg-[var(--panini-red)] rounded-t"
                style={{ height: `${pct}%` }}
              />
              <div className="absolute -top-5 left-1/2 -translate-x-1/2 text-[10px] font-mono opacity-0 group-hover:opacity-100 whitespace-nowrap">
                {d.count}
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex justify-between mt-1 text-[9px] text-[var(--smoke)] font-mono">
        <span>{series[0]?.date}</span>
        <span>{series[series.length - 1]?.date}</span>
      </div>
    </div>
  );
}


export default function QuoteAnalytics() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/admin/quotes/analytics/daily?days=30");
        if (alive) setData(r.data);
      } catch (e) {
        if (alive) setError(formatApiError(e?.response?.data?.detail) || "Failed to load analytics.");
      }
    })();
    return () => { alive = false; };
  }, []);

  if (error) {
    return (
      <div className="text-xs text-red-800" data-testid="quote-analytics-error">
        {error}
      </div>
    );
  }
  if (!data) {
    return (
      <div className="text-xs text-[var(--smoke)]" data-testid="quote-analytics-loading">
        <Loader2 className="animate-spin inline mr-1" size={14} /> Loading analytics…
      </div>
    );
  }

  return (
    <section className="mb-6" data-testid="quote-analytics">
      <div className="kicker kicker-cream">Overview</div>
      <h2 className="font-display text-xl md:text-2xl mt-1 mb-3">Quote Analytics</h2>
      <div className="flex flex-wrap gap-3">
        <KpiCard label="Quotes Today"     value={data.quotes_today}       testid="qa-today" />
        <KpiCard label="Quotes Yesterday" value={data.quotes_yesterday}   testid="qa-yesterday" />
        <KpiCard label="Last 7 Days"      value={data.last_7_days_total}  testid="qa-last7" />
        <KpiCard label="Last 30 Days"     value={data.last_30_days_total} testid="qa-last30" />
        <KpiCard label="Avg / Day"        value={data.avg_per_day.toFixed(1)} testid="qa-avg" />
      </div>
      <DailyBarChart series={data.daily_series} />
      <div className="mt-1 text-[10px] text-[var(--smoke)]">
        All daily counts use the America/New_York calendar. One quote = one count (deduplicated by public_id).
      </div>
    </section>
  );
}
